import { useState } from "react";
import { MessageCircle, Plus, Trash2, Gift } from "lucide-react";
import { api } from "../lib/api";
import { fdate, fmonth, money, run, thisMonth, useLoad } from "../lib/util";
import { waLink } from "../lib/whatsapp";
import { Alert, Badge, Btn, Card, Empty, Field, Input, Select, Table } from "../components/kit";

const FILL = [["gestor", "Gestor sozinho (R$ 89,90)"], ["equipe", "Equipe MapIA (R$ 149,90)"]];
const ST = { a_cobrar: ["A cobrar", "amber"], pago: ["Pago", "green"], gratis: ["Grátis (oferta)", "green"] };

const chargeMsg = (m, s) =>
  `Olá, ${s?.name || ""}! O mapa de merenda de ${fmonth(m.month)} no MapIA (${m.filled_by === "equipe" ? "preenchido pela equipe MapIA" : "preenchido pelo gestor"}) tem o valor de ${money(m.price)}, com vencimento em ${fdate(m.due_date)}. O mapa é liberado para confecção após a confirmação do pagamento.`;

function OfferCard({ offer }) {
  if (!offer) return null;
  return (
    <div className="grid sm:grid-cols-4 gap-4">
      {[["Vagas da oferta usadas", `${offer.used} de ${offer.total}`], ["Vagas restantes", offer.remaining], ["Escolas que já receberam o grátis", offer.free_schools.filter((s) => s.produced).length], ["Solicitações novas", offer.leads_new]].map(([l, v]) => (
        <Card key={l} data-testid={`offer-kpi-${l}`}>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{l}</div>
          <div className="font-heading text-2xl font-bold text-[#0B192C] mt-1">{v}</div>
        </Card>
      ))}
    </div>
  );
}

function Summary({ schools, maps, offer }) {
  const closed = new Set(offer.free_schools.filter((s) => s.produced).map((s) => `${s.id}|${s.free_map_month}`));
  return (
    <Table testid="maps-summary-table">
      <thead><tr><th>Escola</th><th>Vaga da oferta</th><th>Mapas</th><th>Mapa grátis</th><th>Total pago</th><th>A cobrar</th></tr></thead>
      <tbody>
        {schools.map((s) => {
          const ms = maps.filter((m) => m.school_id === s.id);
          const sum = (st) => ms.filter((m) => m.status === st).reduce((a, m) => a + m.price, 0);
          return (
            <tr key={s.id} data-testid={`maps-summary-${s.id}`}>
              <td className="font-semibold">{s.name}</td>
              <td>{s.offer_slot ? <Badge tone="green">Sim</Badge> : "—"}</td>
              <td>{ms.length}</td>
              <td>{s.free_map_month ? <span className="flex items-center gap-1"><Gift className="w-4 h-4 text-[#FF6500]" />{fmonth(s.free_map_month)}{!closed.has(`${s.id}|${s.free_map_month}`) && <Badge tone="amber">a fazer</Badge>}</span> : "—"}</td>
              <td>{money(sum("pago"))}</td>
              <td className={sum("a_cobrar") ? "font-semibold text-amber-700" : ""}>{money(sum("a_cobrar"))}</td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

function AddMap({ schools, onDone }) {
  const [f, setF] = useState({ school_id: "", month: thisMonth(), filled_by: "gestor" });
  const add = async () => (await run(() => api.post("/admin/maps", f), "Mapa lançado")) && onDone();
  return (
    <div className="grid sm:grid-cols-4 gap-3 items-end bg-white border rounded-xl p-4">
      <Field label="Escola"><Select data-testid="map-add-school" value={f.school_id} onChange={(e) => setF({ ...f, school_id: e.target.value })} placeholder="Selecione" options={schools.map((s) => [s.id, s.name])} /></Field>
      <Field label="Mês do mapa"><Input data-testid="map-add-month" type="month" value={f.month} onChange={(e) => setF({ ...f, month: e.target.value })} /></Field>
      <Field label="Quem fez o preenchimento"><Select data-testid="map-add-filled" value={f.filled_by} onChange={(e) => setF({ ...f, filled_by: e.target.value })} options={FILL} /></Field>
      <Btn data-testid="map-add-button" onClick={add}><Plus className="w-4 h-4" />Lançar mapa</Btn>
    </div>
  );
}

function MapRow({ m, s, onChange }) {
  const upd = async (body, ok) => (await run(() => api.put(`/admin/maps/${m.id}`, body), ok)) && onChange();
  const del = async () => window.confirm("Remover este mapa da cobrança?") && (await run(() => api.delete(`/admin/maps/${m.id}`), "Mapa removido")) && onChange();
  const wa = waLink(s?.whatsapp, chargeMsg(m, s));
  const [label, tone] = ST[m.status];
  return (
    <tr data-testid={`map-row-${m.id}`}>
      <td className="font-semibold">{s?.name || "—"}</td>
      <td>{fmonth(m.month)}</td>
      <td><Select className="py-1" data-testid={`map-filled-${m.id}`} value={m.filled_by} onChange={(e) => upd({ filled_by: e.target.value }, "Preenchimento atualizado")} options={FILL} /></td>
      <td data-testid={`map-price-${m.id}`} className="font-semibold">{m.free ? "Grátis" : money(m.price)}</td>
      <td>{fdate(m.due_date)}</td>
      <td><Badge tone={tone}>{label}</Badge>{m.paid_at && <div className="text-xs text-slate-500">{fdate(m.paid_at)}</div>}</td>
      <td className="whitespace-nowrap">
        {m.status === "a_cobrar" && <Btn variant="ghost" data-testid={`map-paid-${m.id}`} onClick={() => upd({ status: "pago" }, "Pagamento registrado: mapa liberado")}>Marcar pago</Btn>}
        {m.status === "pago" && <Btn variant="ghost" data-testid={`map-unpaid-${m.id}`} onClick={() => upd({ status: "a_cobrar" }, "Voltou para a cobrar")}>Desfazer</Btn>}
        {m.status === "a_cobrar" && (wa ? <a href={wa} target="_blank" rel="noreferrer"><Btn variant="ghost" data-testid={`map-charge-${m.id}`}><MessageCircle className="w-4 h-4 text-green-600" />Cobrar</Btn></a> : <Badge tone="amber">Sem WhatsApp</Badge>)}
        <Btn variant="ghost" title="Remover mapa" aria-label="Remover mapa" data-testid={`map-delete-${m.id}`} onClick={del}><Trash2 className="w-4 h-4 text-red-600" /></Btn>
      </td>
    </tr>
  );
}

export default function MapsTab() {
  const [data, reload] = useLoad(async () => {
    const [maps, schools, offer] = await Promise.all([api.get("/admin/maps"), api.get("/admin/schools"), api.get("/admin/offer")]);
    return { maps: maps.data, schools: schools.data, offer: offer.data };
  }, []);
  const [only, setOnly] = useState("");
  if (!data) return null;
  const school = (id) => data.schools.find((s) => s.id === id);
  const maps = data.maps.filter((m) => !only || m.status === only);
  return (
    <div className="space-y-5 pt-6" data-testid="maps-tab">
      <OfferCard offer={data.offer} />
      <Alert tone="info">Cada escola convidada com a oferta ganha o primeiro mapa feito pelo gestor sozinho. Mapas feitos pela equipe MapIA são sempre cobrados (R$ 149,90). A cobrança vence no último dia do mês do mapa, e o gestor só gera o mapa daquele mês depois do pagamento registrado aqui.</Alert>
      <h3 className="font-heading font-bold text-lg text-[#0B192C]">Resumo por escola</h3>
      <Summary schools={data.schools} maps={data.maps} offer={data.offer} />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h3 className="font-heading font-bold text-lg text-[#0B192C]">Mapas e cobrança</h3>
        <div className="w-48"><Select data-testid="maps-filter" value={only} onChange={(e) => setOnly(e.target.value)} placeholder="Todos" options={Object.entries(ST).map(([k, [l]]) => [k, l])} /></div>
      </div>
      <AddMap schools={data.schools} onDone={reload} />
      <Table testid="maps-table">
        <thead><tr><th>Escola</th><th>Mês</th><th>Quem fez o preenchimento</th><th>Valor</th><th>Vencimento</th><th>Situação</th><th /></tr></thead>
        <tbody>{maps.map((m) => <MapRow key={m.id} m={m} s={school(m.school_id)} onChange={reload} />)}</tbody>
      </Table>
      {!maps.length && <Empty>Nenhum mapa lançado.</Empty>}
    </div>
  );
}
